// ============================================================================
// Subida de la caja de contingencia (modo contingencia, paso 3d · oct-2026).
//
// No pinta nada. Cuando hay conexión y en este equipo quedan pedidos de la
// caja sin subir, los manda al servidor (src/lib/subida-contingencia.ts):
//   - al volver la conexión, o al cambiar algo en la caja con conexión;
//   - otra vez enseguida si mientras subía se agregó algo;
//   - y cada 30 s mientras quede algo, por si una vuelta se cortó sin que
//     cambiara el estado de la conexión.
// Lo rechazado queda en "Requiere revisión" y no se reintenta solo.
// ============================================================================
import { useEffect, useRef } from 'react';
import { useUser } from '../../context/user-context';
import { useConexion } from '../../hooks/use-conexion';
import { useCaja, subirAhora } from '../../contingencia/caja';
import { necesitaSubida } from '../../../lib/caja-contingencia';

const ESPERA_MS = 1_500;
const OTRA_VUELTA_MS = 1_000;
const CADA_MS = 30_000;

export function SubidaContingencia() {
  const { user } = useUser();
  const { estado } = useConexion();
  const { pedidos } = useCaja();
  const hayConexion = estado !== 'sin_conexion' && estado !== 'comprobando';
  const pendientes = pedidos.filter(necesitaSubida);
  // Cambia cuando el cajero agrega, quita o cobra algo de lo pendiente.
  const huella = pendientes.map(p => `${p.id}:${p.estado}:${p.lineas.length}:${p.lineas.reduce((s, l) => s + l.cantidad, 0)}`).join('|');
  const puede = !!user && hayConexion && pendientes.length > 0;
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const vivo = useRef(true);

  useEffect(() => () => {
    vivo.current = false;
    if (timer.current) clearTimeout(timer.current);
  }, []);

  const correr = async () => {
    const r = await subirAhora();
    // Algo quedó (un plato agregado mientras subía): otra vuelta enseguida.
    if (vivo.current && r && !r.detenida && r.quedan > 0 && r.confirmadas > 0) {
      timer.current = setTimeout(() => { void correr(); }, OTRA_VUELTA_MS);
    }
  };

  useEffect(() => {
    if (!puede) return;
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => { void correr(); }, ESPERA_MS);
    const reloj = setInterval(() => { void correr(); }, CADA_MS);
    return () => {
      clearInterval(reloj);
      if (timer.current) clearTimeout(timer.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [puede, huella, user?.id]);

  return null;
}
